import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { jobService } from "../services/jobService";
import { light as C, MONO, radius } from "../../hiring/theme";

// Deleting a job, with the damage stated first.
//
// WHY IT FETCHES BEFORE IT ASKS
// "This cannot be undone" is not information. "This deletes 14 candidates and
// 3 completed interviews" is, and it is the difference between a confirmation
// somebody reads and one they click through. The numbers come from the server,
// because only the server knows how many of those candidates are also sitting
// in another job and will therefore survive.
//
// WHY YOU HAVE TO TYPE THE TITLE
// Reserved for the case that actually warrants it: a job with people in it. An
// empty job deletes on one click, because making somebody type a name to
// discard something that contains nothing trains them to type names without
// reading. The friction has to land where the loss is.

export default function DeleteJobDialog({ open, jobId, jobTitle, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await jobService.previewDeleteJob(jobId);
      setPreview(res?.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.statusMessage || "Could not read what this would delete.");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (open) {
      setTyped("");
      setError("");
      setPreview(null);
      load();
    }
  }, [open, load]);

  const hasPeople = (preview?.applications || 0) > 0;
  const confirmed = !hasPeople || typed.trim() === (preview?.job_title || jobTitle || "").trim();

  const run = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await jobService.deleteJob(jobId);
      if (res?.data?.success) onDeleted?.(res.data.statusMessage);
      else setError(res?.data?.statusMessage || "Could not delete the job.");
    } catch (e) {
      setError(e?.response?.data?.statusMessage || "Could not delete the job.");
    } finally {
      setBusy(false);
    }
  };

  const Line = ({ n, one, many, tone }) =>
    n > 0 ? (
      <Box sx={{ display: "flex", gap: 1.25, alignItems: "baseline" }}>
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: 14,
            fontWeight: 700,
            color: tone || C.ink,
            minWidth: 28,
            textAlign: "right",
          }}
        >
          {n}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: C.muted }}>{n === 1 ? one : many}</Typography>
      </Box>
    ) : null;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: radius.lg } }}
      // A MUI Dialog renders through a portal, and React sends events up the
      // COMPONENT tree rather than the DOM tree. Mounted from the job card,
      // that means a click on "Delete permanently" also reaches the card's
      // onClick — which navigates into the very job being deleted, landing you
      // on "This job no longer exists".
      //
      // The portal makes it look contained. It is not.
      onClick={(e) => e.stopPropagation()}
    >
      <DialogContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 2 }}>
          <WarningAmberRoundedIcon sx={{ color: "#dc2626", fontSize: 22, mt: 0.25 }} />
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.ink, lineHeight: 1.3 }}>
              Delete “{preview?.job_title || jobTitle}”?
            </Typography>
            <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.5, lineHeight: 1.55 }}>
              This is permanent. There is no undo and no archive.
            </Typography>
          </Box>
        </Stack>

        {loading && (
          <Box sx={{ py: 4, display: "grid", placeItems: "center" }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!loading && preview && (
          <>
            <Box sx={{ bgcolor: C.wash, borderRadius: radius.md, p: 2, mb: 2 }}>
              <Typography sx={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: ".09em",
                                textTransform: "uppercase", color: C.faint, mb: 1.25 }}>
                This deletes
              </Typography>
              <Stack spacing={0.75}>
                <Line n={1} one="job" many="job" />
                <Line n={preview.applications} one="application" many="applications" />
                <Line n={preview.candidates_to_delete} one="candidate" many="candidates" tone="#b91c1c" />
                <Line n={preview.interviews} one="interview and its recording"
                      many="interviews and their recordings" tone="#b91c1c" />
              </Stack>

              {preview.applications === 0 && (
                <Typography sx={{ fontSize: 13, color: C.muted }}>
                  Nobody has applied to this job yet.
                </Typography>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: ".09em",
                                textTransform: "uppercase", color: C.faint, mb: 0.75 }}>
                Kept
              </Typography>
              <Stack spacing={0.5}>
                {preview.template_kept != null && (
                  <Typography sx={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>
                    • The job template this was created from. You can open the role again from it.
                  </Typography>
                )}
                {preview.candidates_in_other_jobs > 0 && (
                  <Typography sx={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>
                    • {preview.candidates_in_other_jobs} candidate
                    {preview.candidates_in_other_jobs === 1 ? " who is" : "s who are"} also in
                    another job. They are removed from this one only.
                  </Typography>
                )}
                <Typography sx={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>
                  • Your AI usage and credit history. Deleting a job does not erase what was spent.
                </Typography>
              </Stack>
            </Box>

            {hasPeople && (
              <TextField
                fullWidth
                size="small"
                autoComplete="off"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={preview.job_title}
                label={`Type the job title to confirm`}
                sx={{ mb: 1.5 }}
              />
            )}
          </>
        )}

        {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: radius.sm }}>{error}</Alert>}

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button onClick={onClose} disabled={busy}
                  sx={{ textTransform: "none", fontWeight: 650, color: C.muted }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy || loading || !confirmed || !preview}
            onClick={run}
            startIcon={busy ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#dc2626",
              "&:hover": { bgcolor: "#b91c1c" },
            }}
          >
            {busy ? "Deleting…" : "Delete permanently"}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
